import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
  TextField,
  Chip,
  Stack,
  CircularProgress,
  Button,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import axios from "axios";
import { useUniboxColors } from "../../useUniboxColors";
import AttachmentThumbnail from "../preview/AttachmentThumbnail";
import AttachmentPreviewModal from "../preview/AttachmentPreviewModal";
import { authHeaders } from "../preview/attachmentUtils";

const API = process.env.REACT_APP_API_URL;

const TYPE_FILTERS = [
  { id: "all", label: "Todos" },
  { id: "pdf", label: "PDF" },
  { id: "image", label: "Imágenes" },
  { id: "excel", label: "Excel" },
  { id: "word", label: "Word" },
  { id: "other", label: "Otros" },
];

function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function FilterPill({ label, selected, onClick, c, maxWidth }) {
  return (
    <Chip
      label={label}
      size="small"
      onClick={onClick}
      sx={{
        flexShrink: 0,
        height: 28,
        fontWeight: 700,
        fontSize: 11.5,
        maxWidth: maxWidth || "none",
        bgcolor: selected ? c.accent : c.panel,
        color: selected ? "#fff" : c.textPrimary,
        border: `1px solid ${selected ? c.accent : c.border}`,
        "&:hover": {
          bgcolor: selected ? c.accentBorder : c.cardHover,
        },
        "& .MuiChip-label": {
          px: 1.25,
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      }}
    />
  );
}

export default function AttachmentsLibrary({
  open,
  onClose,
  accounts = [],
  folder = "inbox",
  onOpenInConversation,
}) {
  const c = useUniboxColors();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [accountFilter, setAccountFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!open) return;
    setPage(1);
  }, [open, debouncedSearch, accountFilter, typeFilter, folder]);

  const loadPage = useCallback(
    async (pageNum, append = false) => {
      if (!API) return;
      if (append) setLoadingMore(true);
      else setLoading(true);

      try {
        const res = await axios.get(`${API}/emails/attachments/browse`, {
          params: {
            q: debouncedSearch || undefined,
            account: accountFilter || undefined,
            type: typeFilter === "all" ? undefined : typeFilter,
            folder: folder === "trash" ? "trash" : "inbox",
            page: pageNum,
            limit: 50,
          },
        });

        const nextItems = res.data?.items || [];
        setTotal(Number(res.data?.total || 0));
        setItems((prev) => (append ? [...prev, ...nextItems] : nextItems));
        setPage(pageNum);
      } catch (err) {
        console.error("Error cargando adjuntos:", err);
        if (!append) {
          setItems([]);
          setTotal(0);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [debouncedSearch, accountFilter, typeFilter, folder]
  );

  useEffect(() => {
    if (!open) return;
    loadPage(1, false);
  }, [open, loadPage]);

  const hasMore = items.length < total;

  const accountOptions = useMemo(
    () =>
      accounts.map((a) => ({
        email: String(a.email || "").toLowerCase(),
        label: a.email,
      })),
    [accounts]
  );

  const handleDownload = async (item, event) => {
    event?.stopPropagation?.();
    try {
      const res = await fetch(`${API}/emails/attachment/${item.id}/download`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = item.filename || "adjunto";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      window.alert("No se pudo descargar el archivo.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          bgcolor: c.card,
          border: `1px solid ${c.border}`,
          boxShadow: c.shadowMd,
          overflow: "hidden",
          maxHeight: "92vh",
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2.25,
          borderBottom: `1px solid ${c.border}`,
          background: `linear-gradient(135deg, ${c.accentSoft} 0%, ${c.card} 60%)`,
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: "12px",
            bgcolor: c.accentSoft,
            border: `1px solid ${c.accentBorder}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <FolderOpenOutlinedIcon sx={{ color: c.accent, fontSize: 24 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{ fontWeight: 800, fontSize: 20, letterSpacing: -0.4, color: c.textPrimary }}
          >
            Archivos adjuntos
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: c.textMuted, mt: 0.35, lineHeight: 1.4 }}>
            Haz clic en una miniatura para ver el archivo. Imágenes y PDF con vista previa.
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            mt: -0.25,
            color: c.textMuted,
            "&:hover": { bgcolor: c.cardHover },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          px: 3,
          py: 2.25,
          display: "flex",
          flexDirection: "column",
          gap: 1.75,
        }}
      >
        <TextField
          size="small"
          fullWidth
          placeholder="Buscar por nombre, asunto o remitente…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 20, color: c.textDim }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: c.panel,
              fontSize: 13.5,
              "& fieldset": { borderColor: c.border },
              "&:hover fieldset": { borderColor: c.accentBorder },
              "&.Mui-focused fieldset": { borderColor: c.accent },
            },
          }}
        />

        <Box
          sx={{
            display: "flex",
            gap: 0.75,
            overflowX: "auto",
            pb: 0.25,
            flexWrap: "nowrap",
            "&::-webkit-scrollbar": { height: 4 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: c.border,
              borderRadius: 4,
            },
          }}
        >
          <FilterPill
            label="Todas las cuentas"
            selected={!accountFilter}
            onClick={() => setAccountFilter("")}
            c={c}
          />
          {accountOptions.map(({ email, label }) => (
            <FilterPill
              key={email}
              label={label}
              selected={accountFilter === email}
              onClick={() => setAccountFilter(email)}
              c={c}
              maxWidth={200}
            />
          ))}
        </Box>

        <Box sx={{ display: "flex", gap: 0.65, flexWrap: "wrap" }}>
          {TYPE_FILTERS.map((t) => (
            <FilterPill
              key={t.id}
              label={t.label}
              selected={typeFilter === t.id}
              onClick={() => setTypeFilter(t.id)}
              c={c}
            />
          ))}
        </Box>

        <Typography sx={{ fontSize: 12, color: c.textDim, fontWeight: 600 }}>
          {loading ? "Cargando…" : `${total.toLocaleString("es-ES")} archivo${total === 1 ? "" : "s"}`}
        </Typography>

        <Box
          sx={{
            flex: 1,
            minHeight: 320,
            maxHeight: "52vh",
            overflowY: "auto",
            borderRadius: "12px",
            border: `1px solid ${c.border}`,
            bgcolor: c.isDark ? c.panel : "#ffffff",
            p: 1.5,
          }}
        >
          {loading && items.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress size={28} sx={{ color: c.accent }} />
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 8, px: 2 }}>
              <Typography sx={{ fontWeight: 700, color: c.textPrimary, fontSize: 15 }}>
                No hay archivos
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: c.textMuted, mt: 0.75 }}>
                Prueba otro filtro o sincroniza tus cuentas.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                  sm: "repeat(3, 1fr)",
                  md: "repeat(4, 1fr)",
                  lg: "repeat(5, 1fr)",
                },
                gap: 1.25,
              }}
            >
              {items.map((item) => (
                <Box
                  key={item.id}
                  onClick={() => setPreviewItem(item)}
                  sx={{
                    borderRadius: "12px",
                    border: `1px solid ${c.border}`,
                    overflow: "hidden",
                    cursor: "pointer",
                    bgcolor: c.card,
                    transition: "border-color 0.12s, box-shadow 0.12s, transform 0.12s",
                    "&:hover": {
                      borderColor: c.accent,
                      boxShadow: c.shadowSm,
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      p: 1.25,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      bgcolor: c.panel,
                      minHeight: 108,
                    }}
                  >
                    <AttachmentThumbnail
                      attachmentId={item.id}
                      filename={item.filename}
                      mimetype={item.mimetype}
                      size={96}
                    />
                  </Box>

                  <Box sx={{ px: 1.15, py: 1.1 }}>
                    <Typography
                      title={item.filename}
                      sx={{
                        fontWeight: 700,
                        fontSize: 12,
                        color: c.textPrimary,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.filename}
                    </Typography>
                    <Typography
                      title={item.subject || "(Sin asunto)"}
                      sx={{
                        fontSize: 10.5,
                        color: c.textMuted,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        mt: 0.2,
                      }}
                    >
                      {item.subject || "(Sin asunto)"}
                    </Typography>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mt: 0.65 }}
                    >
                      <Typography sx={{ fontSize: 9.5, color: c.textDim }}>
                        {formatBytes(item.size)}
                      </Typography>
                      <Stack direction="row" spacing={0}>
                        <IconButton
                          size="small"
                          title="Descargar"
                          onClick={(e) => handleDownload(item, e)}
                          sx={{
                            p: 0.35,
                            color: c.textMuted,
                            "&:hover": { color: c.accent },
                          }}
                        >
                          <DownloadOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        {onOpenInConversation ? (
                          <IconButton
                            size="small"
                            title="Abrir conversación"
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenInConversation(item);
                            }}
                            sx={{
                              p: 0.35,
                              color: c.textMuted,
                              "&:hover": { color: c.accent },
                            }}
                          >
                            <OpenInNewOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        ) : null}
                      </Stack>
                    </Stack>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {hasMore && !loading ? (
          <Button
            fullWidth
            variant="outlined"
            disabled={loadingMore}
            onClick={() => loadPage(page + 1, true)}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              fontSize: 13,
              py: 1,
              borderRadius: "12px",
              borderColor: c.border,
              color: c.accent,
              "&:hover": {
                borderColor: c.accent,
                bgcolor: c.accentSoft,
              },
            }}
          >
            {loadingMore
              ? "Cargando…"
              : `Cargar más (${items.length} de ${total.toLocaleString("es-ES")})`}
          </Button>
        ) : null}
      </DialogContent>

      <AttachmentPreviewModal
        open={Boolean(previewItem)}
        item={previewItem}
        onClose={() => setPreviewItem(null)}
        onOpenInConversation={onOpenInConversation}
      />
    </Dialog>
  );
}
